import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { components } from "@libertify/api-types/media";
import axiosInstance from "@/services/api";
import { useSession } from "hooks/useSession";
import type { CreatorProfile } from "../types";

type CreatorProfileUpdateRequest =
  components["schemas"]["CreatorProfileUpdateRequest"];

type UpdateCreatorProfileVariables = {
  profileId: string;
  values: CreatorProfileUpdateRequest;
};

/**
 * Partially updates a Creator Profile: only the fields sent change. Resolves
 * once the resolved creator-profiles queries have refetched, so callers read
 * the new profile (and its recomputed archetype) right after.
 */
export const useUpdateCreatorProfile = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useSession();
  const organizationId = currentOrganization?.id;

  return useMutation<CreatorProfile, Error, UpdateCreatorProfileVariables>({
    mutationFn: async ({ profileId, values }) => {
      const response = await axiosInstance.patch<CreatorProfile>(
        `/media/${organizationId}/creator-profiles/${profileId}`,
        values
      );
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: (query) =>
          JSON.stringify(query.queryKey).includes("creator-profiles"),
      }),
  });
};
